import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { motion } from 'framer-motion';

// Mapbox access token - get yours from https://account.mapbox.com/access-tokens/
// Using a public token for development - replace with your own token for production
const MAPBOX_TOKEN = 'pk.eyJ1IjoidWJpdXFpdHlnbGFzc2NvcnAiLCJhIjoiY21nMGc0NzVwMGQ5aTJtb2Zhd3B1dml1OCJ9.zn_jlab_xf8xGG6usUvGkw';

// Alternative: Try using a different public token or create your own
// const MAPBOX_TOKEN = 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

interface MarketData {
  city: string;
  name: string;
  state: string;
  status: 'active' | 'inactive';
  service_radius: number;
  market_map: {
    type: 'Polygon' | 'GeometryCollection';
    coordinates?: number[][][]; // For simple Polygon format
    geometries?: Array<{ // For GeometryCollection format
      type: 'Polygon';
      coordinates: number[][][];
    }>;
  };
  // Additional fields from new API structure
  id?: string;
  date_created?: string;
  date_updated?: string;
  sort?: number | null;
  tesseract_update_requested?: boolean;
  user_created?: string;
}

interface MarketMapProps {
  markets: MarketData[];
}

export default function MarketMap({ markets }: MarketMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);

  // Helper: extract all polygons as an array of polygons (each polygon is an array of linear rings)
  const getPolygons = (market: MarketData): number[][][][] => {
    if (market.market_map.type === 'Polygon' && market.market_map.coordinates) {
      return [market.market_map.coordinates];
    }
    if (market.market_map.type === 'GeometryCollection' && market.market_map.geometries) {
      return (market.market_map.geometries || [])
        .filter(g => g && g.type === 'Polygon' && Array.isArray(g.coordinates))
        .map(g => g.coordinates);
    }
    return [];
  };

  // Helper: build a GeoJSON geometry (Polygon or MultiPolygon) from list of polygons
  const buildGeometryFromPolygons = (polygons: number[][][][]): { type: 'Polygon' | 'MultiPolygon'; coordinates: any } => {
    if (polygons.length <= 1) {
      return { type: 'Polygon', coordinates: polygons[0] };
    }
    return { type: 'MultiPolygon', coordinates: polygons };
  };

  // Helper: compute the (absolute) area of a polygon outer ring using shoelace
  const computeRingArea = (ring: number[][]): number => {
    let sum = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[i + 1];
      sum += xi * yj - xj * yi;
    }
    return Math.abs(sum) / 2;
  };

  // Helper: pick the largest polygon by outer ring area
  const getLargestPolygon = (polygons: number[][][][]): number[][][] => {
    if (polygons.length === 0) return [] as unknown as number[][][];
    let largest = polygons[0];
    let maxArea = computeRingArea(polygons[0][0] || []);
    for (let i = 1; i < polygons.length; i++) {
      const area = computeRingArea(polygons[i][0] || []);
      if (area > maxArea) {
        maxArea = area;
        largest = polygons[i];
      }
    }
    return largest;
  };

	useEffect(() => {
		// Don't initialize map if we don't have market data yet
		if (markets.length === 0) {
			console.log('Waiting for market data...');
			return;
		}

		// Clean up existing map if it exists
		if (map.current) {
			console.log('Cleaning up existing map before re-initializing');
			if ((map.current as any).pulseInterval) {
				clearInterval((map.current as any).pulseInterval);
			}
			if ((map.current as any).spinInterval) {
				clearInterval((map.current as any).spinInterval);
			}
			map.current.remove();
			map.current = null;
		}

		console.log('Initializing map with token:', MAPBOX_TOKEN);
		console.log('Market data available:', markets.length, 'markets');

		try {
			mapboxgl.accessToken = MAPBOX_TOKEN;
			console.log('Setting Mapbox token:', MAPBOX_TOKEN);

			// Use continental US bounds for better overview
			const usBounds = new mapboxgl.LngLatBounds();
			usBounds.extend([-125, 25]); // Southwest corner
			usBounds.extend([-66, 49]);  // Northeast corner

			map.current = new mapboxgl.Map({
				container: mapContainer.current!,
				style: 'mapbox://styles/mapbox/streets-v12', // High-quality 3D vector maps
				center: [-120, 35], // Start over California
				zoom: 1.5, // Globe view
				pitch: 60, // Angled to show atmosphere
				bearing: 0, // North up
				projection: 'globe'
			});

			// Add space background after style loads
			map.current.on('style.load', () => {
				// Add atmosphere effect
				map.current!.setFog({
					color: 'rgb(186, 210, 235)', // Blue atmosphere
					'high-color': 'rgb(36, 92, 223)',
					'horizon-blend': 0.02,
					'space-color': 'rgb(11, 11, 25)',
					'star-intensity': 0.6
				});
			});

			// Globe spinning animation - starts immediately
			let spinPhase = 0;
			const spinInterval = setInterval(() => {
				spinPhase += 0.5; // West to east rotation
				map.current!.easeTo({
					bearing: spinPhase,
					duration: 0
				});
			}, 50);

			// Store spin interval for cleanup
			(map.current as any).spinInterval = spinInterval;

			// Transition to markets view when data loads
			map.current.on('load', () => {
				// Wait for market data to be ready, then transition
				const checkForData = () => {
					if (markets.length > 0) {
						// Stop spinning
						clearInterval(spinInterval);
						
						// Smooth transition to US markets view
						map.current!.easeTo({
							center: [-98, 39], // Center of continental US
							zoom: 3, // Show all US states
							pitch: 0, // Flat view for markets
							bearing: 0, // North up
							duration: 4000,
							essential: true,
							easing: (t) => t * (2 - t) // Smooth ease-out
						});
					} else {
						// Keep spinning until data loads
						setTimeout(checkForData, 100);
					}
				};
				
				// Start checking for data
				checkForData();
			});

			console.log('Map instance created:', map.current);

			// Add error handler before load event
			map.current.on('error', (e) => {
				console.error('Mapbox error before load:', e);
				setMapError(true);
				setIsLoaded(true);
			});

			// Add timeout to detect if map never loads
			const loadTimeout = setTimeout(() => {
				console.error('Map failed to load within 5 seconds');
				setMapError(true);
				setIsLoaded(true);
			}, 5000);

			map.current.on('load', () => {
				console.log('Map loaded successfully');
				clearTimeout(loadTimeout);
				setIsLoaded(true);
			
				// Add market polygons
				console.log('Processing markets for map rendering:', markets.length);
				markets.forEach((market, index) => {
					const sourceId = `market-${index}`;
					const layerId = `market-layer-${index}`;
					
					console.log(`Adding market ${index + 1}/${markets.length}: ${market.name} (${market.status})`);
					console.log('Market map type:', market.market_map.type);
					
					// Extract all polygons for this market
					const polygons = getPolygons(market);
					console.log('Market polygons count:', polygons.length);
					
					// Validate coordinates before adding
					if (!polygons || polygons.length === 0) {
						console.error(`Invalid market data for ${market.name}:`, market);
						return;
					}
					
					// Add source with error handling
					try {
						map.current!.addSource(sourceId, {
							type: 'geojson',
							data: {
								type: 'Feature',
								geometry: buildGeometryFromPolygons(polygons),
								properties: {
									name: market.name,
									status: market.status,
									service_radius: market.service_radius
								}
							}
						});
						console.log(`Successfully added source for ${market.name}`);
					} catch (error) {
						console.error(`Failed to add source for ${market.name}:`, error);
						return;
					}

					// Add fill layer with gradient effect and fade-in
					try {
						map.current!.addLayer({
							id: layerId,
							type: 'fill',
							source: sourceId,
							paint: {
								'fill-color': market.status === 'active' 
									? '#0099FF' // Baby blue for active
									: '#9900FF', // Royal purple for inactive
								'fill-opacity': 0 // Start invisible
							}
						});
						console.log(`Successfully added fill layer for ${market.name}`);
					} catch (error) {
						console.error(`Failed to add fill layer for ${market.name}:`, error);
						return;
					}

					// Fade in the fill layer
					setTimeout(() => {
						map.current!.setPaintProperty(layerId, 'fill-opacity', market.status === 'active' ? 0.4 : 0.2);
					}, 3000 + (index * 200)); // Staggered fade-in

					// Add pulsing glow effect layer for active markets
					if (market.status === 'active') {
						map.current!.addLayer({
							id: `${layerId}-pulse`,
							type: 'line',
							source: sourceId,
							paint: {
								'line-color': '#0099FF',
								'line-width': 12,
								'line-opacity': 0, // Start invisible
								'line-blur': 8
							}
						});

						// Fade in pulse layer
						setTimeout(() => {
							map.current!.setPaintProperty(`${layerId}-pulse`, 'line-opacity', 0.6);
						}, 3000 + (index * 200));
					}

					// Add glow effect layer
					map.current!.addLayer({
						id: `${layerId}-glow`,
						type: 'line',
						source: sourceId,
						paint: {
							'line-color': market.status === 'active' 
								? '#0099FF' 
								: '#9900FF',
							'line-width': 8,
							'line-opacity': 0, // Start invisible
							'line-blur': 4
						}
					});

					// Add main stroke layer
					map.current!.addLayer({
						id: `${layerId}-stroke`,
						type: 'line',
						source: sourceId,
						paint: {
							'line-color': market.status === 'active' 
								? '#0099FF' 
								: '#9900FF',
							'line-width': 3,
							'line-opacity': 0 // Start invisible
						}
					});

					// Fade in stroke and glow layers
					setTimeout(() => {
						map.current!.setPaintProperty(`${layerId}-glow`, 'line-opacity', 0.3);
						map.current!.setPaintProperty(`${layerId}-stroke`, 'line-opacity', 0.9);
					}, 3000 + (index * 200));

					// Calculate proper centroid using the largest polygon's outer ring
					const largestPolygon = getLargestPolygon(polygons);
					const coordinatesForCentroid = largestPolygon[0];
					
					// Calculate area and centroid using shoelace formula
					let area = 0;
					let centerLng = 0;
					let centerLat = 0;
					
					for (let i = 0; i < coordinatesForCentroid.length - 1; i++) {
						const j = (i + 1) % (coordinatesForCentroid.length - 1);
						const xi = coordinatesForCentroid[i][0];
						const yi = coordinatesForCentroid[i][1];
						const xj = coordinatesForCentroid[j][0];
						const yj = coordinatesForCentroid[j][1];
						
						const cross = xi * yj - xj * yi;
						area += cross;
						centerLng += (xi + xj) * cross;
						centerLat += (yi + yj) * cross;
					}
					
					area = area / 2;
					centerLng = centerLng / (6 * area);
					centerLat = centerLat / (6 * area);
					
					// Calculate market size for indicator scaling
					const bounds = {
						minLng: Math.min(...coordinatesForCentroid.map(c => c[0])),
						maxLng: Math.max(...coordinatesForCentroid.map(c => c[0])),
						minLat: Math.min(...coordinatesForCentroid.map(c => c[1])),
						maxLat: Math.max(...coordinatesForCentroid.map(c => c[1]))
					};
					
					const marketWidth = bounds.maxLng - bounds.minLng;
					const marketHeight = bounds.maxLat - bounds.minLat;
					const marketSize = Math.sqrt(marketWidth * marketWidth + marketHeight * marketHeight);
					
					// Scale indicator size based on market perimeter (12px to 32px)
					const baseSize = 12;
					const maxSize = 32;
					const indicatorSize = Math.max(baseSize, Math.min(maxSize, baseSize + (marketSize * 2)));

					// Market indicators are now handled as map layers below

					// Add market center as a map layer instead of marker
					const centerSourceId = `market-center-${index}`;
					const centerLayerId = `market-center-layer-${index}`;
					
					// Add center point source
					map.current!.addSource(centerSourceId, {
						type: 'geojson',
						data: {
							type: 'Feature',
							geometry: {
								type: 'Point',
								coordinates: [centerLng, centerLat]
							},
							properties: {
								name: market.name,
								status: market.status,
								service_radius: market.service_radius,
								size: indicatorSize
							}
						}
					});

					// Add center point layer with fade-in
					map.current!.addLayer({
						id: centerLayerId,
						type: 'circle',
						source: centerSourceId,
						paint: {
							'circle-radius': [
								'interpolate',
								['linear'],
								['get', 'size'],
								12, 6,
								32, 16
							],
							'circle-color': market.status === 'active' ? '#0099FF' : '#9900FF',
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 3,
							'circle-opacity': 0 // Start invisible
						}
					});

					// Add pulsing effect for active markets
					if (market.status === 'active') {
						map.current!.addLayer({
							id: `${centerLayerId}-pulse`,
							type: 'circle',
							source: centerSourceId,
							paint: {
								'circle-radius': [
									'interpolate',
									['linear'],
									['get', 'size'],
									12, 12,
									32, 32
								],
								'circle-color': '#0099FF',
								'circle-opacity': 0, // Start invisible
								'circle-stroke-width': 0
							}
						});
					}

					// Fade in center indicators
					setTimeout(() => {
						map.current!.setPaintProperty(centerLayerId, 'circle-opacity', 0.9);
						if (market.status === 'active') {
							map.current!.setPaintProperty(`${centerLayerId}-pulse`, 'circle-opacity', 0.3);
						}
					}, 3000 + (index * 200));

					// Add click handler for market tour
					map.current!.on('click', centerLayerId, (e) => {
						// Create popover
						const popup = new mapboxgl.Popup({
							closeButton: true,
							closeOnClick: false,
							className: 'market-popup'
						})
						.setLngLat([centerLng, centerLat])
						.setHTML(`
							<div class="p-4">
								<h3 class="text-lg font-bold text-gray-900">${market.name}</h3>
								<p class="text-sm text-gray-600 mt-1">
									Status: <span class="font-semibold ${market.status === 'active' ? 'text-green-600' : 'text-gray-500'}">
										${market.status === 'active' ? 'Active' : 'Inactive'}
									</span>
								</p>
								<p class="text-sm text-gray-600">Service Radius: ${market.service_radius.toLocaleString()} mi</p>
								<button 
									class="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
									onclick="startMarketTour('${market.name}', ${centerLng}, ${centerLat})"
								>
									Take Market Tour
								</button>
							</div>
						`)
						.addTo(map.current!);

						// Zoom to state level for better context
						map.current!.easeTo({
							center: [centerLng, centerLat],
							zoom: 6, // State level zoom
							pitch: 45,
							duration: 2000,
							essential: true,
							easing: (t) => t * (2 - t)
						});
					});

					// Add hover effect
					map.current!.on('mouseenter', centerLayerId, () => {
						map.current!.getCanvas().style.cursor = 'pointer';
					});

					map.current!.on('mouseleave', centerLayerId, () => {
						map.current!.getCanvas().style.cursor = '';
					});

					// Pulse effects are now handled by map layers above
				});
				
				console.log(`Finished processing ${markets.length} markets for map rendering`);

				// Add pulsing animation for active market borders and centers
				const activeMarkets = markets.filter(m => m.status === 'active');
				if (activeMarkets.length > 0) {
					let pulsePhase = 0;
					const pulseInterval = setInterval(() => {
						pulsePhase += 0.05; // Slower pulse for smoother effect
						activeMarkets.forEach((market, index) => {
							// Pulse border
							const layerId = `market-layer-${index}`;
							if (map.current!.getLayer(`${layerId}-pulse`)) {
								const opacity = Math.sin(pulsePhase) * 0.3 + 0.5;
								const width = Math.sin(pulsePhase) * 3 + 12;
								map.current!.setPaintProperty(`${layerId}-pulse`, 'line-opacity', opacity);
								map.current!.setPaintProperty(`${layerId}-pulse`, 'line-width', width);
							}
							
							// Pulse center indicator
							const centerLayerId = `market-center-layer-${index}`;
							if (map.current!.getLayer(`${centerLayerId}-pulse`)) {
								const opacity = Math.sin(pulsePhase) * 0.2 + 0.3;
								const radius = Math.sin(pulsePhase) * 4 + 12;
								map.current!.setPaintProperty(`${centerLayerId}-pulse`, 'circle-opacity', opacity);
								map.current!.setPaintProperty(`${centerLayerId}-pulse`, 'circle-radius', radius);
							}
						});
					}, 50); // Faster update for smoother animation
					
					// Store interval for cleanup
					(map.current as any).pulseInterval = pulseInterval;
				}

				// Error handler and timeout already added above
			});

		} catch (error) {
			console.error('Error initializing map:', error);
			console.error('Error details:', {
				message: error instanceof Error ? error.message : 'Unknown error',
				stack: error instanceof Error ? error.stack : undefined,
				token: MAPBOX_TOKEN.substring(0, 20) + '...'
			});
			setMapError(true);
			setIsLoaded(true); // Set loaded even on error to hide loading spinner
		}

		// Add market tour function to global scope
		(window as any).startMarketTour = (marketName: string, lng: number, lat: number) => {
			console.log(`Starting tour for ${marketName} at [${lng}, ${lat}]`);
			
			// Create a smooth tour animation with state-level views
			const tourPoints = [
				{ center: [lng, lat], zoom: 7, pitch: 45, duration: 2000 },
				{ center: [lng + 0.5, lat + 0.3], zoom: 8, pitch: 30, duration: 1500 },
				{ center: [lng - 0.3, lat - 0.2], zoom: 7.5, pitch: 60, duration: 1500 },
				{ center: [lng, lat], zoom: 6, pitch: 45, duration: 2000 }
			];

			let currentPoint = 0;
			const runTour = () => {
				if (currentPoint < tourPoints.length) {
					const point = tourPoints[currentPoint];
					map.current!.easeTo({
						center: point.center as [number, number],
						zoom: point.zoom,
						pitch: point.pitch,
						duration: point.duration,
						essential: true,
						easing: (t) => t * (2 - t)
					});
					currentPoint++;
					setTimeout(runTour, point.duration + 500);
				}
			};
			
			runTour();
		};

		return () => {
			// Clear all animation intervals
			if (map.current) {
				if ((map.current as any).pulseInterval) {
					clearInterval((map.current as any).pulseInterval);
				}
				if ((map.current as any).spinInterval) {
					clearInterval((map.current as any).spinInterval);
				}
				map.current.remove();
			}
		};
	}, [markets]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8 }}
      className="w-full"
    >
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gradient mb-4">Service Areas</h3>
        <p className="text-gray-600 mb-4">
          Ubiquity AutoGlass provides automotive glass services across multiple markets in Texas, Arizona, and Oklahoma.
        </p>
        
        {/* Legend */}
        <div className="flex gap-6 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-babyblue"></div>
            <span className="text-sm font-medium">Active Markets</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-royalpurple"></div>
            <span className="text-sm font-medium">Inactive Markets</span>
          </div>
        </div>
      </div>

      <div className="relative">
        {mapError ? (
          <div 
            className="w-full h-[500px] md:h-[600px] lg:h-[700px] rounded-xl overflow-hidden border border-gray-200 shadow-lg flex items-center justify-center" 
            style={{ 
              backgroundColor: '#000011'
            }}
          >
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-babyblue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-babyblue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold mb-2">Service Areas Map</h4>
              <p className="text-gray-600 mb-4">Interactive map temporarily unavailable</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-center p-3 bg-white rounded-lg">
                  <div className="font-semibold text-babyblue">Active Markets</div>
                  <div className="text-lg font-bold">{markets.filter(m => m.status === 'active').length}</div>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <div className="font-semibold text-royalpurple">Inactive Markets</div>
                  <div className="text-lg font-bold">{markets.filter(m => m.status === 'inactive').length}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div 
              ref={mapContainer} 
              className="w-full h-[500px] md:h-[600px] lg:h-[700px] rounded-xl overflow-hidden border border-gray-200 shadow-lg relative"
              style={{ 
                backgroundColor: '#000011'
              }}
            />
            
            {!isLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading map...</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Market Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-babyblue">{markets.filter(m => m.status === 'active').length}</div>
          <div className="text-sm text-gray-600">Active Markets</div>
        </div>
        <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-royalpurple">{markets.filter(m => m.status === 'inactive').length}</div>
          <div className="text-sm text-gray-600">Inactive Markets</div>
        </div>
        <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-orangepeel">{markets.length}</div>
          <div className="text-sm text-gray-600">Total Markets</div>
        </div>
        <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-primary">{markets.reduce((sum, m) => sum + m.service_radius, 0)}</div>
          <div className="text-sm text-gray-600">Total Service Radius</div>
        </div>
      </div>
    </motion.div>
  );
}
